import React, { createContext, useState, useContext } from "react";
import { AuthContext } from "../AuthContext";
import Notification from "../components/Notification";
import { API_BASE_URL } from "../config";

export const UploadContext = createContext({
  uploadTrack: async () => { },
  updateTrack: async () => { },
  addNotification: () => { },
  AddCoverArtFromTrack: async () => { },
  uploadAdvertisementImage: async () => { },
  uploadPlaylistBackgroundImage: async () => "",
});

const UploadProvider = ({ children }) => {
  const { authData } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);
  };

  const showSuccessNotification = (message) => {
    addNotification(message, "success");
  };

  const showErrorNotification = (message) => {
    addNotification(message, "error");
  };

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
  };

  const uploadTrack = async (albumId, formData) => {
    if (!albumId) {
      throw new Error("Album ID is required to upload a track.");
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/albums/${albumId}/tracks`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        let errorMessage = "Unknown error occurred";
        try {
          const errorResponse = await response.json();
          errorMessage = errorResponse.message || JSON.stringify(errorResponse);
        } catch (jsonError) {
          errorMessage = response.statusText;
        }
        throw new Error(`Server Error: ${errorMessage}`);
      }

      const result = await response.json();
      showSuccessNotification(`Track uploaded successfully: ${result.title}`);
      return result;
    } catch (error) {
      showErrorNotification(`Failed to upload track: ${error.message}`);
      throw error;
    }
  };

  const updateTrack = async (trackId, formData) => {
    if (!trackId) {
      throw new Error("Track ID is required to update a track.");
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/tracks/${trackId}/trackfile`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorMessage = response.statusText || "Unknown error occurred";
        throw new Error(`Server Error: ${errorMessage}`);
      }

      showSuccessNotification("Track updated successfully");
      return { success: true, status: response.status };
    } catch (error) {
      showErrorNotification(`Failed to update track: ${error.message}`);
      throw error;
    }
  };

  const AddCoverArtFromTrack = async (trackId, pictureData) => {
    if (!trackId || !pictureData) {
      showErrorNotification("Track ID and picture data are required");
      return;
    }

    try {
      // Convert base64 string to a Blob
      const byteCharacters = atob(pictureData.split(",")[1]); // Remove the "data:image/...;base64," part
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "image/jpeg" });

      // Create a file object
      const file = new File([blob], `${trackId}.jpg`, { type: "image/jpeg" });

      // Prepare FormData
      const formData = new FormData();
      formData.append("file", file);

      // Upload to the given API
      const response = await fetch(`${API_BASE_URL}/api/storage/uploadFile/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = "Failed to upload cover art";
        try {
          const errorResponse = await response.json();
          errorMessage = errorResponse.message || JSON.stringify(errorResponse);
        } catch (jsonError) {
          errorMessage = response.statusText;
        }
        throw new Error(errorMessage);
      }

      showSuccessNotification("Cover art uploaded successfully");
      return;
    } catch (error) {
      showErrorNotification(`Failed to upload cover art: ${error.message}`);
      throw error;
    }
  };

  /**
   * POST /api/storage/uploadAdvertentie – uploads an ad/screensaver image.
   * @param {File} file
   * @returns {Promise<string>} the public URL the server answered ("" when it
   *   sent none); the file name is its last path segment
   */
  const uploadAdvertisementImage = async (file) => {
    if (!file) {
      showErrorNotification("File is required to upload an advertisement image.");
      return "";
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API_BASE_URL}/api/storage/uploadAdvertentie`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authData.accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = "Failed to upload advertisement image";
        try {
          const errorResponse = await response.json();
          errorMessage = errorResponse.message || JSON.stringify(errorResponse);
        } catch (jsonError) {
          errorMessage = response.statusText;
        }
        throw new Error(errorMessage);
      }

      // The server answers the public URL of the uploaded file as plain text
      // (the S3 upload + AD_RESOURCE registration finish asynchronously). The
      // picker uses this to show the image right away (DJB-233).
      let fileUrl = "";
      try {
        fileUrl = (await response.text()).trim().replace(/^"|"$/g, "");
      } catch {
        fileUrl = "";
      }
      showSuccessNotification("Advertisement image uploaded successfully");
      return fileUrl;
    } catch (error) {
      showErrorNotification(`Failed to upload advertisement image: ${error.message}`);
      throw error;
    }
  };

  /**
   * POST /api/storage/uploadPlaylistBackgroundImage — uploads a playlist
   * background to the playlistbackgrounds/ folder the player reads from. The
   * server answers the public URL as plain text; the bare file name (what
   * PLAYLIST.backgroundImage stores) is resolved, '' on failure.
   */
  const uploadPlaylistBackgroundImage = async (file) => {
    if (!file) {
      showErrorNotification('File is required to upload a playlist background.');
      return '';
    }
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_BASE_URL}/api/storage/uploadPlaylistBackgroundImage`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authData.accessToken}` },
        body: formData,
      });
      if (!response.ok) {
        let errorMessage = 'Failed to upload playlist background';
        try {
          const errorResponse = await response.json();
          errorMessage = errorResponse.message || JSON.stringify(errorResponse);
        } catch {
          errorMessage = response.statusText;
        }
        throw new Error(errorMessage);
      }
      const url = (await response.text()).trim().replace(/^"|"$/g, '');
      const fileName = decodeURIComponent(url.split('/').pop() ?? '');
      showSuccessNotification('Playlist background uploaded');
      return fileName;
    } catch (error) {
      showErrorNotification(error.message);
      return '';
    }
  };

  return (
    <UploadContext.Provider value={{ uploadTrack, updateTrack, addNotification, AddCoverArtFromTrack, uploadAdvertisementImage, uploadPlaylistBackgroundImage }}>
      {children}
      <div className="notification-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </UploadContext.Provider>
  );
};

export default UploadProvider;
